/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { MOMENT_INPUT_FORMATS } from 'constants';

import isNil from 'lodash/isNil';
import moment from 'moment-timezone';

// Escape regular expression characters for my search
RegExp.escape = str => String(str).replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');

/**
 * Locale formats a datestamp
 * @param  {number} timestamp expects seconds since the Epoch
 * @param {string} formatString moment date format string
 * @return {string} returns the locale formatted string of datestamp
 */
export function formatDatestamp(timestamp, formatString = MOMENT_INPUT_FORMATS.SECONDS) {
  return (
    typeof timestamp === 'number' ? moment.utc(timestamp * 1000).local() : moment(timestamp)
  ).format(formatString);
}

/**
 * Locale formats a datestamp from milliseconds or in ISO_8601
 * @param  {number|string} timestamp expects milliseconds since the Epoch or the SQL string in ISO_8601 format
 * @param {string} locale, the locale to format the date
 * @param {string} timeZone, the timeZone to format for the date
 * @param {string} formatString moment date format string
 * @return {string} returns locale formatted string of datestamp
 */
export function formatTimestampToLocalTimestamp(
  timestamp,
  locale = 'en',
  timeZone,
  formatString = MOMENT_INPUT_FORMATS.SECONDS
) {
  moment.locale(locale);
  if (!isNil(timeZone)) {
    moment.tz.setDefault(timeZone);
  }

  let updatedTimestamp = timestamp;
  if (typeof timestamp !== 'number') {
    updatedTimestamp = moment(timestamp, moment.ISO_8601);
  }
  return moment(updatedTimestamp).format(formatString);
}

/**
 * Locale formats a timestamp from milliseconds since Epoch
 */
export function formatTimeWithPM(time) {
  return time.format('hh:mm a');
}

export function formatTimestampMS(timestamp) {
  return formatTimeWithPM(moment.utc(timestamp).local());
}

export function formatTime(time) {
  return time.format('hh:mm');
}
